import { Link } from 'react-router-dom';

import { parseOwnerRepoCommit } from '@/components/Rules/RulesEvaluatorPane.utils';
import useToast from '@/components/Toast/useToast';
import {
  EProviderSecretName,
  useHasWorkspaceSecrets,
} from '@/hooks/useHasWorkspaceSecrets';
import { useOrganizationId, usePromptOptimization } from '@/hooks/useSwr';
import {
  EPromptOptimizationAlgorithm,
  OptimizationConfigForForm,
  PromptOptimizationRequest,
  PromptimConfig,
} from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { appPromptsIndexPath } from '@/utils/constants';

function formValuesToRequest(
  optimizePayload: OptimizationConfigForForm
): PromptOptimizationRequest {
  const { config, ...rest } = optimizePayload;
  const { algorithm } = optimizePayload;

  if (algorithm === EPromptOptimizationAlgorithm.PROMPTIM) {
    return {
      ...rest,
      config: config as PromptimConfig,
    };
  }

  return optimizePayload as PromptOptimizationRequest;
}

export const useOptimize = () => {
  const { createToast } = useToast();
  const organizationId = useOrganizationId();

  const { openSecretsRef, missingSecrets } = useHasWorkspaceSecrets([
    EProviderSecretName.OPENAI,
  ]);

  const { trigger: optimizeTrigger, isMutating: isOptimizeLoading } =
    usePromptOptimization({
      onError: (error) => {
        createToast({
          title: 'Failed to optimize',
          description: error.message,
          error: true,
          type: 'error',
        });
      },
    });

  const handleOptimize = async (optimizePayload: OptimizationConfigForForm) => {
    if (missingSecrets()) {
      openSecretsRef.current?.();
      return;
    }

    const promptOptimizationRequest: PromptOptimizationRequest =
      formValuesToRequest(optimizePayload);

    const [, repo] = parseOwnerRepoCommit(
      promptOptimizationRequest.prompt_name
    );

    const job = await optimizeTrigger({
      json: promptOptimizationRequest,
    });

    if (job?.optimization_job_id) {
      createToast({
        title: `Successfully set up prompt optimization for ${promptOptimizationRequest.prompt_name}`,
        description: (
          <Link
            to={addQueryParams(`/${appPromptsIndexPath}/${repo}`, {
              organizationId,
              jobId: job.optimization_job_id,
              tab: 2,
            })}
          >
            Logs
          </Link>
        ),
        type: 'success',
      });
    }
  };

  return {
    openSecretsRef,
    handleOptimize,
    isOptimizeLoading,
  };
};
